import * as __req_0 from "../../../../__mocks__/pd2532/frequentlog.json";
import * as __req_1 from "../../../../__mocks__/pd2532/processsummary.json";
import * as __req_2 from "../../../../__mocks__/pd2532/historics.json";
import React, { useState, useEffect, useRef } from "react";
import { catchError, tap, filter } from "rxjs/operators";
import last from "lodash/last";
import head from "lodash/head";
import get from "lodash/get";
import cloneDeep from "lodash/cloneDeep";
import UserActionManager from "../UserActionManager/UserActionManager";
import ChartHeader from "../ChartHeader/ChartHeader";
import Chart from "../Chart/Chart";
import ConnectionLostOverlay from "./ConnectionLostOverlay/ConnectionLostOverlay";
import * as historicSessionService from "@brewery/services/historicSessionService";
import PHASE_STATUS from "@common/constants/phaseStatus";
import mqttService from "../../../mqttService";
import mqttUtil from "../../../mqttUtil";
import * as mqttTopics from "@common/constants/mqttTopics";
import chartUtil from "./chartUtil";
import { getPhaseTimeDetails } from "../../../../util/timelineHelper";
import PhasesProgress from "../PhasesProgress/PhasesProgress";
import {
  MAX_BREWING_TEMP,
  MAX_FERMENTING_TEMP,
  MAX_SERVING_TEMP,
  MAX_CLEANING_TEMP,
} from "@common/constants/chartOptions";
import { MACH_STATES } from "@common/constants/sessionConstants";
import LocalErrorBoundary from "./LocalErrorBoundary";

/**
 * Listens to the frequentlog channel and takes care of useractions
 */
const ChartManager = ({ active, session, options, type, open, mqtt }) => {
  const [progressWidths, setProgressWidths] = useState([]);
  const [progressPercentage, setProgressPercentage] = useState([]);
  const [chartOptions, setChartOptions] = useState(options);
  const [lastFrequentLog, setLastFrequentLog] = useState();
  const [brokerMachineConnected, setBrokerMachineConnected] = useState(true);
  const [phaseStart, setPhaseStart] = useState(null);
  const [phaseEnd, setPhaseEnd] = useState(null);

  const chartOptionsRef = useRef(chartOptions);
  useEffect(() => {
    chartOptionsRef.current = chartOptions;
  }, [chartOptions]);
  /**
   * Using a local frequent logs array, we can circumvent the constant
   * execution of useEffect. We only execute useEffect when the active state changes
   * and not when frequentlogs update.
   */
  let localFrequentLogs = [];
  let chartOptionsCache = JSON.parse(JSON.stringify(options)); // make a deep copy of options prop
  let processSummary = null;
  let historics = null;

  useEffect(() => {
    let subscriptions = [];

    if (active) {
      setBrokerMachineConnected(false);
      fetchHistorics()
        .then((response) => {
          historics = response.data;
          if (historics && historics.length > 0) {
            const sorted = [...historics].sort((a, b) => a.t - b.t);
            setPhaseStart(sorted[0].t);
            setPhaseEnd(sorted[sorted.length - 1].t);
          }
        })
        .catch(() => {
          historics = null;
        });

      const frequentLogResponseSubscription =
        mqttService.frequentLogResponse$.subscribe((frequentlogResponse) => {
          if (frequentlogResponse && frequentlogResponse.processSummaryList) {
            processSummary = frequentlogResponse.processSummaryList;
          }
        });
      const notificationSubscription = mqttService.notification$.subscribe(
        (notification) => {
          if (session.id === notification.session) {
            // Handle Process summary change
            if (mqttUtil.processSummaryChanged(notification)) {
              // TODO: UPDATE CHART OPTIONS
              processSummary = notification.processSummaryList;
            }
          }
        },
      );

      const logsTopic = mqttTopics.logsTopic + session.device.uuid;
      let frequentlogWithTimeout$ = mqttUtil
        .withFrequentLogRetry(mqttService.frequentLog$, {
          timeoutMs: 5000,
          onRetry: () => {
            setBrokerMachineConnected(false);
            // Don't keep asking a device whose logs topic the broker refused
            // us (no active session on this keg) -- that was the feed for the
            // reconnect storm. mqttService retries the subscribe with backoff.
            if (
              mqttService.isConnected() &&
              !mqttService.isTopicRefused(logsTopic)
            ) {
              const { uuid } = session.device;
              mqttService.publishRequestFrequentLogs(uuid, mqtt.clientUUID);
            }
          },
        })
        .pipe(
          tap((log) => {
            if (log.session && log.session !== session.id) {
              // find if there are any subscbiptions which are on different devices from current session device
              const unused = mqttUtil.findNoNUsedFrequentLogSubscriptions(
                session.device.uuid,
                mqttService.subscriptions,
              );
              unused.forEach((topic) => {
                mqttService.unsubscribe(topic);
              });

              throw new Error("session.ID and and log.sessio are different");
            }
          }),
          // `log.session === 0` never matched: session is a proto3 scalar, so a
          // zero is omitted on the wire and jspb's toObject() yields UNDEFINED,
          // not 0. Any device streaming session-less logs was therefore dropped
          // silently -- no emission, so brokerMachineConnected stayed false and
          // the panel sat behind "Connection lost, reconnecting..." while frames
          // arrived at 1Hz. Accept both spellings of "no session".
          filter(
            (log) =>
              log.session === 0 ||
              log.session === undefined ||
              log.session === session.id,
          ),
          // A log from another session throws above; resubscribe to keep the
          // stream alive, but the retry policy (with backoff) stays in charge.
          catchError(() => frequentlogWithTimeout$),
        );

      let frequentlogSubscription = frequentlogWithTimeout$.subscribe(
        (frequentlog) => {
          // frequentlog = (__req_0.default !== undefined ? __req_0.default : __req_0);
          // processSummary = (__req_1.default !== undefined ? __req_1.default : __req_1);
          // historics = (__req_2.default !== undefined ? __req_2.default : __req_2);
          setBrokerMachineConnected(true);
          setLastFrequentLog(frequentlog);
          const currentLog = frequentlog.logsList[0];
          if (currentLog && currentLog.timestampMs) {
            setPhaseEnd(currentLog.timestampMs);
            setPhaseStart((prev) => prev || currentLog.timestampMs);
          }
          localFrequentLogs = [...localFrequentLogs, frequentlog.logsList[0]];

          const updatedChartOptions = chartUtil.updateChartOptions(
            frequentlog,
            cloneDeep(chartOptionsRef.current),
            processSummary,
            historics,
          );

          const maxTemp = getMaxTemp(type);
          updatedChartOptions.series[1].data = chartUtil.filterOutOfRangeData(
            maxTemp,
            updatedChartOptions.series[1].data,
          );
          updatedChartOptions.series[0].data = chartUtil.filterOutOfRangeData(
            maxTemp,
            updatedChartOptions.series[0].data,
          );
          updatedChartOptions.series[3].data = chartUtil.filterOutOfRangeData(
            maxTemp,
            updatedChartOptions.series[3].data,
          );

          let chartAxisData = []
            .concat(updatedChartOptions.series[3].data) // historics
            .concat(updatedChartOptions.series[0].data) // realtime
            .concat(updatedChartOptions.series[1].data); // prediction
          const sortedChartAxisData = chartAxisData
            .filter(
              (x) =>
                x && x.value && x.value[0] !== undefined && x.value[0] !== null,
            )
            .sort((x, y) => {
              const tx =
                x.value[0] instanceof Date ? x.value[0].getTime() : x.value[0];
              const ty =
                y.value[0] instanceof Date ? y.value[0].getTime() : y.value[0];
              return tx - ty || x.phase - y.phase;
            });

          let groupedDataByTime = [];
          sortedChartAxisData.forEach((entry) => {
            let lastGroup = groupedDataByTime[groupedDataByTime.length - 1];
            if (!lastGroup || lastGroup.phase !== entry.phase) {
              groupedDataByTime.push({
                phase: entry.phase,
                data: [entry],
              });
            } else {
              lastGroup.data.push(entry);
            }
          });

          let fullPeriod = 0;
          const firstProcess = head(groupedDataByTime.filter(Boolean));
          const lastProcess = last(groupedDataByTime);
          const firstProcessDate = get(firstProcess, "data.0.value.0");
          const lastProcessDate = get(last(lastProcess?.data), "value.0");

          if (firstProcessDate && lastProcessDate) {
            fullPeriod = lastProcessDate.getTime() - firstProcessDate.getTime();
          }

          const percentages = [];

          groupedDataByTime.forEach((entry, index) => {
            let startDate = null;
            let endDate = null;
            if (entry.data.length > 1) {
              startDate = entry.data[0].value[0];
              endDate = entry.data[entry.data.length - 1].value[0];
            } else {
              const prevPeriod = groupedDataByTime[index - 1];
              startDate = get(prevPeriod, [
                "data",
                get(prevPeriod, "data.length") - 1,
                "value",
                0,
              ]);
              endDate = get(entry, "data.0.value.0");
            }

            if (endDate && startDate) {
              const period = endDate.getTime() - startDate.getTime();
              const percentage = (period / fullPeriod) * 100;
              percentages.push({
                phase: entry.phase,
                percentage,
                startDate,
                endDate,
              });
            }
          });
          // remove items with small percentage
          // and combine two phases when they are the same after removing empty one
          const percentagesCombined = percentages
            .filter((p) => p.percentage > 0.9)
            .reduce((prev, current) => {
              const lastItem = prev[prev.length - 1];
              if (lastItem && lastItem.phase === current.phase) {
                prev[prev.length - 1].percentage += current.percentage;
                prev[prev.length - 1].endDate = current.endDate;
              } else {
                prev.push(current);
              }
              return prev;
            }, []);

          // sometimes calculations are not very correct,
          // in case total phases is less than 100% we incrase the size of last phase.
          const percentagesSum = percentagesCombined.reduce(
            (prev, current) => prev + current.percentage,
            0,
          );
          if (percentagesSum < 99 && last(percentagesCombined)) {
            const difference = 100 - percentagesSum;
            const lastItem = last(percentagesCombined);
            lastItem.percentage = lastItem.percentage + difference;
          }

          const progressPercentages = [];
          const currentTimestampNormalized =
            frequentlog?.logsList?.[0]?.timestampMs || 0;

          percentagesCombined.forEach((entry) => {
            let progress = 0;
            const { startDate, endDate, phase } = entry;

            if (
              typeof startDate.getTime === "function" &&
              typeof endDate.getTime === "function" &&
              currentTimestampNormalized > startDate.getTime() &&
              currentTimestampNormalized < endDate.getTime()
            ) {
              const duration = endDate.getTime() - startDate.getTime();

              progress =
                duration > 0
                  ? ((currentTimestampNormalized - startDate.getTime()) /
                      duration) *
                    100
                  : 100;
            } else if (currentTimestampNormalized > startDate.getTime()) {
              progress = 100;
            } else {
              progress = 0;
            }

            progressPercentages.push({
              phase,
              progress,
            });
          });

          setProgressPercentage(progressPercentages);
          setProgressWidths(percentagesCombined);
          setChartOptions(updatedChartOptions);
        },
      );
      subscriptions = [
        frequentLogResponseSubscription,
        notificationSubscription,
        frequentlogSubscription,
      ];
    } else {
      fetchHistorics()
        .then((response) => {
          historics = response.data;
          if (historics && historics.length > 0) {
            const sorted = [...historics].sort((a, b) => a.t - b.t);
            setPhaseStart(sorted[0].t);
            setPhaseEnd(sorted[sorted.length - 1].t);
          }
          const updatedChartOptions = chartUtil.updateChartHistoricsOnly(
            chartOptionsCache,
            historics,
          );
          setChartOptions(updatedChartOptions);
        })
        .catch(() => {
          historics = null;
        });
    }
    return () => {
      setChartOptions(options);
      setLastFrequentLog(null);
      subscriptions.forEach((subscription) => {
        if (subscription) {
          subscription.unsubscribe();
        }
      });
    };
  }, [active, session]);

  const fetchHistorics = () => {
    let getHistorics;
    switch (type) {
      case PHASE_STATUS.BREWING:
        getHistorics = historicSessionService.getHistoricBrewing;
        break;
      case PHASE_STATUS.FERMENTATION:
        getHistorics = historicSessionService.getHistoricFermenting;
        break;
      case PHASE_STATUS.SERVING:
        getHistorics = historicSessionService.getHistoricServing;
        break;
      case PHASE_STATUS.CLEANING:
        getHistorics = historicSessionService.getHistoricCIP;
    }

    return getHistorics(session.id);
  };

  const isUpdating = session
    ? session.device.current_state === MACH_STATES.MACH_STATE_UPDATING
    : null;
  const hasChartData = chartOptions?.series?.some(
    (s) => s.data && s.data.length > 0,
  );

  const phaseDetails = getPhaseTimeDetails(session, type, phaseStart, phaseEnd);

  return (
    <LocalErrorBoundary>
      <div style={{ marginBottom: "20px" }}>
        <UserActionManager
          lastFrequentLog={lastFrequentLog}
          active={active}
          session={session}
        />
        <ChartHeader
          lastFrequentLog={lastFrequentLog}
          session={session}
          type={type}
          active={active}
          startTime={phaseDetails.startTime}
          endTime={phaseDetails.endTime}
          status={phaseDetails.status}
          duration={phaseDetails.durationFormatted}
        />
        <div>
          {type !== PHASE_STATUS.SERVING && active && (
            <PhasesProgress
              progressWidths={progressWidths}
              progressPercentages={progressPercentage}
            />
          )}
          {active || hasChartData ? (
            <Chart active={active} option={chartOptions} open={open} />
          ) : (
            <div
              style={{
                padding: "30px 20px",
                background: "rgba(255, 255, 255, 0.02)",
                border: "1px dashed rgba(255, 255, 255, 0.12)",
                borderRadius: "6px",
                marginTop: "10px",
                textAlign: "center",
                color: "#a5a6a9",
                fontFamily: "Gotham-Book, sans-serif",
                fontSize: "13px",
              }}
            >
              No temperature or gravity logs were recorded for this phase.
            </div>
          )}
        </div>
        {!brokerMachineConnected && active && !isUpdating && (
          <ConnectionLostOverlay />
        )}
      </div>
    </LocalErrorBoundary>
  );
};

export default ChartManager;

const getMaxTemp = (type) => {
  switch (type) {
    case PHASE_STATUS.BREWING:
      return MAX_BREWING_TEMP;
    case PHASE_STATUS.FERMENTATION:
      return MAX_FERMENTING_TEMP;
    case PHASE_STATUS.SERVING:
      return MAX_SERVING_TEMP;
    case PHASE_STATUS.CLEANING:
      return MAX_CLEANING_TEMP;
  }

  return 100000;
};
